import React, {useEffect, useMemo, useRef, useState} from 'react';
import {Alert, Spin} from "antd";
import {Formik} from "formik";
import FormInput from "../../../components/UI/FormInput";
import {
    Btn,
    FlexColumnSection, FlexDiv,
    GreyTextClickable,
    Header,
    Wrapper
} from "../../../styles/globalStyles";
import * as yup from "yup";
import useScreenSize from "../../../helpers/hooks/useScreenSize";
import {
    LOGIN_MODAL, ONE_TIME_PASSWORD_MODAL,
    openModal
} from "@arboxappv4/shared/src/redux/modalManagerSlice";
import {useDispatch} from "react-redux";
import {apiAction} from "@arboxappv4/shared/src/helpers/HTTP";
import {t} from "i18next";
import CustomModal from "../../../components/UI/CustomModal";
import Logo from "../../../components/Logo";

const ForgotPassword = (props) => {
    const { isOpen, onClose, email } = props;
    const validationSchema = useMemo(() => yup.object().shape({email: yup.string().email(t('email-invalid-format')).required(t('email-required-field'))}), []);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [submitType, setSubmitType] = useState(null);
    const dispatch = useDispatch()
    const {isMobile} = useScreenSize();
    const formikRef = useRef();

    useEffect(() => {
        if(submitType) {
            validate()
        }
    }, [submitType]);

    const validate = async () => {
        const invalidProps = await formikRef.current?.validateForm()
        if(Object.keys(invalidProps).length === 0) {
            formikRef.current?.handleSubmit()
        } else {
            setSubmitType(null)
        }
    }

    const resetPassword = async (values) => {
        try {
            setError(null)
            setLoading(true)
            await checkIfValidToReset(values.email)
            if(submitType === 'reset') {
                await apiAction(`user/resetPassword`, 'post', values);
                setSuccess(true)
                setLoading(false)
            }
            setSubmitType(null)
        } catch(error) {
            console.log('resetPassword', error)
            setError(error)
            setLoading(false)
            setSubmitType(null)
        }
    }

    const checkIfValidToReset = async (email) => {
        try {
            await apiAction(`site/checkValidForLogin`, 'post', {email});
        } catch (error) {
            console.log('checkIfValidToReset', error)
            throw t(`invalid-reset-${error.error?.messageToUser ?? 'default'}`)
        }
    }

    const goBackToLogin = () => {
        onClose()
        dispatch(openModal({modalName: LOGIN_MODAL, props: {email: email}}))
    }

    return (
        <CustomModal open={isOpen} onCancel={goBackToLogin} width={isMobile ? '100%' : '450px'}>
            <Wrapper>
                <Spin spinning={loading}>
                    <Formik initialValues={{ email: email ?? ''}} validationSchema={validationSchema} onSubmit={resetPassword} innerRef={formikRef}>
                        {({
                              values,
                              errors,
                              setFieldValue,
                          }) => (
                            <FlexColumnSection gap={'20px'}>
                                {isMobile && <Logo onClick={onClose}/>}
                                <FlexColumnSection gap={'5px'} flex={'none'}>
                                    <Header>{t('reset-password-header')}</Header>
                                    <div>{t('reset-password-subheader')}</div>
                                </FlexColumnSection>
                                {success ?
                                    <Alert type="success" description={(
                                        <FlexColumnSection gap={'5px'} flex={'none'}>
                                            <div>{t('reset-password-success-header')}</div>
                                            <div>{t('reset-password-success-subheader')}</div>
                                        </FlexColumnSection>)}
                                    />
                                    :
                                    <>
                                        <FlexColumnSection gap={'15px'}>
                                            <FormInput label={t('email')} value={values.email} error={errors.email ?? error} onChangeCallback={(val) => setFieldValue('email', val)} inputMode={'email'} type={'email'} autoComplete={'email'} id={'email'}/>
                                        </FlexColumnSection>
                                        <FlexDiv dir={isMobile ? 'column' : 'row'} gap={'15px'}>
                                            <Btn type={'primary'} onClick={() => errors.email ? null : setSubmitType('reset')} width={isMobile ? '100%' : null}>{t('reset-password')}</Btn>
                                        </FlexDiv>
                                    </>
                                }
                                <GreyTextClickable onClick={goBackToLogin}>{t('back-to-login')}</GreyTextClickable>
                            </FlexColumnSection>
                        )}
                    </Formik>
                </Spin>
            </Wrapper>
        </CustomModal>
    );
};

export default ForgotPassword;

